import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import api from "../lib/api";
import { setToken, setUser } from "../lib/auth";
import { toast } from "sonner";

const Login = () => {
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", form);
      setToken(res.data.token);
      setUser(res.data.user);
      if (res.data.reactivated) {
        toast.success("Bon retour ! Votre compte a été réactivé gratuitement. Votre page et vos factures sont restaurées.", { duration: 6000 });
      } else {
        toast.success("Connecté.");
      }
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim().toLowerCase() });
      toast.success(
        "Demande envoyée. Un mot de passe provisoire va vous être transmis par l'administrateur dans les plus brefs délais.",
        { duration: 7000 }
      );
      setForgotOpen(false);
      setForgotEmail("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'envoyer la demande pour le moment.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-5 py-12 marketing-mobile-shrink">
      <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
        <Link to="/" className="inline-flex items-center gap-3 group" data-testid="login-brand">
          <img
            src="/assets/logo.png"
            alt="Le Guide des Entrepreneurs"
            className="h-12 w-auto max-w-[240px] object-contain"
          />
        </Link>
        <h1 className="font-heading font-black text-4xl tracking-tighter mt-8 mb-2 text-[#1E2A5E]" style={{ fontFamily: '"Manrope", sans-serif' }}>Connexion</h1>
        <p className="text-gray-600 mb-8">Accédez à votre espace client pour personnaliser votre page.</p>

        <div className="space-y-5">
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="rounded-none border-gray-300"
              data-testid="login-email"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-sm font-semibold">Mot de passe</Label>
              <button
                type="button"
                onClick={() => {
                  setForgotEmail(form.email || "");
                  setForgotOpen(true);
                }}
                className="text-xs text-[#1E2A5E] font-semibold hover:underline"
                data-testid="forgot-password-btn"
              >
                Mot de passe oublié ?
              </button>
            </div>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              className="rounded-none border-gray-300"
              data-testid="login-password"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full mt-8 rounded-none bg-[#1E2A5E] hover:bg-[#2FA398] py-6 text-base font-semibold"
          data-testid="login-submit"
        >
          {loading ? "Connexion…" : "Se connecter"}
        </Button>

        <p className="text-sm text-gray-500 mt-6 text-center">
          Pas de compte ?{" "}
          <Link
            to="/#annuaire"
            className="text-[#1E2A5E] font-semibold hover:underline"
            data-testid="signup-cta-link"
          >
            Commander un accès
          </Link>
        </p>
      </form>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="rounded-none" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle>Mot de passe oublié</DialogTitle>
            <DialogDescription>
              Indiquez l'adresse email associée à votre compte. Un mot de passe
              provisoire vous sera transmis dans les plus brefs délais par
              l'administrateur du site. Vous pourrez ensuite modifier votre mot
              de passe depuis votre espace client.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForgot} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">Email</Label>
              <Input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                className="rounded-none border-gray-300"
                data-testid="forgot-email-input"
                autoFocus
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-none"
                onClick={() => setForgotOpen(false)}
                data-testid="forgot-cancel-btn"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={forgotLoading || !forgotEmail.trim()}
                className="rounded-none bg-[#1E2A5E] hover:bg-[#2FA398]"
                data-testid="forgot-submit-btn"
              >
                {forgotLoading ? "Envoi…" : "Envoyer la demande"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
